import React, { useContext, useEffect, useState } from "react";
import { SimpleEntry } from "@services/catalog/types";
import ListView from "@common/components/ListDetailViews/ListView";
import { MappingEntryWithIndex, getColumns } from "./colDef";
import {
  ReducerContext,
  ReducerDispatchContext,
} from "@pages/Configuration/DeviceCatalog/context";
import { useTranslation } from "react-i18next";

interface MappingListViewProps {
  setMappingSelected: (entries: number[]) => void;
}

const MappingListView = (props: MappingListViewProps) => {
  const { t } = useTranslation();

  const {
    mappingEntries,
    mappingInherit,
    canEdit,
    canAccessExtendedPropertyMapping,
  } = useContext(ReducerContext);
  const dispatch = useContext(ReducerDispatchContext);
  const { setMappingSelected } = props;
  const [sort, setSort] = useState<Sort<MappingEntryWithIndex>>({
    column: "name",
    direction: "asc",
  });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);

  const handleSelectRow = (selected: MappingEntryWithIndex["index"][]) => {
    setMappingSelected(selected);
  };

  const handleNameChange = (row: MappingEntryWithIndex) => {
    const newMappingEntries = mappingEntries?.map((entry, index) => ({
      ...entry,
      name: index === row.index ? row.name : entry.name,
    }));
    dispatch({ mappingEntries: newMappingEntries, isDataChanged: true });
  };

  const handleValueChange = (row: MappingEntryWithIndex) => {
    const newMappingEntries = mappingEntries?.map((entry, index) => ({
      ...entry,
      value: index === row.index ? row.value : entry.value,
    }));
    dispatch({ mappingEntries: newMappingEntries });
  };

  const setDataChangeFlag = () => {
    dispatch({ isDataChanged: true });
  };

  useEffect(() => {
    sortMapping();
  }, [sort]);

  const sortMapping = () => {
    let tmp = mappingEntries.sort((a, b) => {
      const column = sort.column as keyof SimpleEntry;
      const elemA = a[column];
      const elemB = b[column];
      if (typeof elemA === "string" && typeof elemB === "string") {
        return sort.direction === "asc"
          ? elemA.localeCompare(elemB)
          : elemB.localeCompare(elemA);
      }
      return 0;
    });
    dispatch({ mappingEntries: tmp });
  };

  const columns = getColumns(
    canEdit && canAccessExtendedPropertyMapping,
    t,
    !mappingInherit && canAccessExtendedPropertyMapping,
    handleNameChange,
    handleValueChange,
    mappingEntries.map((f) => f.name ?? ""),
    setDataChangeFlag
  );

  return (
    <ListView
      data={
        mappingEntries?.map((entry, index) => ({
          ...entry,
          index,
        })) ?? []
      }
      columns={columns}
      loading={mappingEntries === undefined}
      total={mappingEntries?.length ?? 0}
      sort={sort}
      onSort={(column, direction) => setSort({ column, direction })}
      setPage={setPage}
      pageSize={pageSize}
      page={page}
      identityKey="index"
      selectable={!mappingInherit && canAccessExtendedPropertyMapping}
      onSelect={handleSelectRow}
      disabledCheckbox={!canEdit}
      sx={{
        flexGrow: 1,
        overflow: "hidden",
        "& > div": { overflowX: "hidden", overflowY: "scroll" },
      }}
      disableColumnResize
    />
  );
};

export default MappingListView;
